import { Root as RadixRoot } from '@radix-ui/react-dropdown-menu';
import {
  type ReactNode,
  useEffect,
  useMemo,
  Children,
  isValidElement,
  cloneElement,
} from 'react';
import { isElement } from 'react-is';

import { type WithChildren } from '@dynatrace/strato-components/core';
import { _mergeProps } from '@dynatrace/strato-components/core';

import { Content, type MenuContentProps } from './Content.js';
import { MenuContext } from './contexts/MenuContext.js';
import { Group } from './Group.js';
import { Intent } from './Intent.js';
import { Item } from './Item.js';
import { ItemIcon } from './ItemIcon.js';
import { ItemTooltip } from './ItemTooltip.js';
import { Label } from './Label.js';
import { Link } from './Link.js';
import { Sub } from './Sub.js';
import { SubContent } from './SubContent.js';
import { SubTrigger } from './SubTrigger.js';
import { Trigger } from './Trigger.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';

// TODO Remove logic for handling tab key as soon as the related bug in the radix-ui
// library has been fixed and we've upgraded to a newer version that contains the fix
// (see https://github.com/radix-ui/primitives/issues/1565 and https://github.com/radix-ui/primitives/pull/1576)

/**
 * @public
 */
export interface MenuProps extends WithChildren {
  /**
   * The open state of the dropdown menu when it is initially rendered. Use when
   * you do not need to control its open state.
   * @defaultValue false
   */
  defaultOpen?: boolean;
  /**
   * The controlled open state of the dropdown menu. Must be used in conjunction
   * with onOpenChange.
   */
  open?: boolean;
  /**
   * Event handler called when the open state of the dropdown menu changes.
   * @param isOpen - true if the menu has been opened
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * The modality of the dialog.
   * When set to true, interaction with outside elements will be disabled and
   * only dialog content will be visible to screen readers.
   * @defaultValue true
   */
  modal?: boolean;
}

type MenuSlots = {
  trigger: ReactNode | null;
  content: ReactNode[];
  freeform: ReactNode[];
};

/** Iterates over child nodes to extract specific component slots. */
function getMenuSlots(children: ReactNode) {
  const slots: MenuSlots = {
    trigger: null,
    content: [],
    freeform: [],
  };

  Children.forEach(children, (child) => {
    if (isElement(child)) {
      switch (child.type) {
        case Trigger:
          slots.trigger = child;
          break;
        case Content:
          slots.content.push(child);
          break;
        default:
          slots.freeform.push(child);
      }
    } else {
      slots.freeform.push(child);
    }
  });

  return slots;
}

/** Iterates over all top-level Content nodes and registers tab key handler. */
function registerTabHandlers(
  children: ReactNode,
  handleTabKeyPressed: () => void,
): ReactNode {
  return Children.map(children, (child: ReactNode) => {
    return isValidElement<MenuContentProps>(child) && child.type === Content
      ? cloneElement(
          child,
          _mergeProps(child.props, {
            _onTabKeyPressed: handleTabKeyPressed,
          }),
          child.props.children,
        )
      : child;
  });
}

/**
 * @internal
 */
const MenuRoot = (props: MenuProps) => {
  const {
    open: value,
    onOpenChange: onChange,
    modal = true,
    defaultOpen = false,
    children,
  } = props;

  const [open, updateOpen] = useControllableState({
    defaultValue: defaultOpen,
    value,
    onChange,
  });

  const slots = useMemo(
    () => getMenuSlots(registerTabHandlers(children, () => updateOpen(false))),
    [children, updateOpen],
  );

  // Closes the Menu on window blur to fix inherent issue with Radix UI keeping submenus open.
  // https://github.com/radix-ui/primitives/issues/1441
  const onWindowBlur = () => {
    updateOpen(false);
  };

  useEffect(() => {
    window.addEventListener('blur', onWindowBlur);
    return () => {
      window.removeEventListener('blur', onWindowBlur);
    };
  });

  const menuContextValue = useMemo(
    () => ({
      isOpen: open,
      isDefaultOpen: defaultOpen,
      setIsOpen: updateOpen,
    }),
    [defaultOpen, open, updateOpen],
  );

  return (
    <MenuContext.Provider value={menuContextValue}>
      <RadixRoot
        open={open}
        onOpenChange={(isOpen) => updateOpen(isOpen)}
        modal={modal}
      >
        {slots.trigger !== null && slots.trigger}
        {open && slots.content}
        {slots.freeform}
      </RadixRoot>
    </MenuContext.Provider>
  );
};

(MenuRoot as typeof MenuRoot & { displayName: string }).displayName = 'Menu';

/**
 * The `Menu` component displays the list of action items that are revealed when
 * the user clicks on the trigger element.
 * @public
 */
export const Menu = Object.assign(MenuRoot, {
  Trigger,
  Content,
  Item,
  ItemTooltip,
  Label,
  Link,
  Group,
  ItemIcon,
  Sub,
  SubTrigger,
  SubContent,
  Intent,
});
